import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material'
import { SearchIcon, UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { cn } from '~/@/lib/utils'
import { useGlobalTheme } from '~/common/hooks/useGlobalTheme'
import { usePage } from '~/common/hooks/usePage'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { AnimatedDossbotIcon } from '~/components/dossbot/AnimatedDossbotIcon'
import { DossLogo } from './DossLogo'
import { FormsLogo } from './logos/FormsLogo'
import { SearchInputModal } from './SearchInputModal'

function BottomNavLabel({ label, isActive }: { label: string; isActive: boolean }) {
  return (
    <div
      className={cn(
        isActive ? 'text-gray-10' : 'text-gray-8',
        'font-size-[10px]',
        'font-style-normal',
        'font-weight-500',
        'leading-4',
        'mt-[8px]',
      )}
    >
      {label}
    </div>
  )
}

/**
 * Bottom navigation bar that appears at the bottom of the screen when in mobile view.
 */
export function BottomNav() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const theme = useGlobalTheme()
  const { t } = useTypedTranslation()
  const { group } = usePage()
  const isFormsActive = group === 'forms'
  const isHomepage = group === 'home'
  const isSettingsActive = group === 'settings'
  const isDossbotActive = group === 'dossbot'
  const [openSearchModal, setOpenSearchModal] = useState(false)

  return (
    <>
      {openSearchModal && (
        <SearchInputModal
          onClose={() => setOpenSearchModal(false)}
          open={openSearchModal}
          openType="click"
        />
      )}
      <Paper
        className={cn(
          'border-gray-4 fixed inset-x-0 bottom-0 z-[1200] h-[84px] border-t',
          isFormsActive ? 'bg-blue-12' : 'bg-gray-12',
        )}
        elevation={1}
      >
        <BottomNavigation
          className="fixed inset-x-0 bottom-0"
          onChange={(_, newValue) => {
            navigate(newValue)
          }}
          showLabels
          sx={{ height: '84px' }}
          value={pathname}
        >
          <BottomNavigationAction
            icon={
              <DossLogo
                className={isHomepage ? 'text-blue-8' : 'text-gray-8'}
                height={20}
                hoverable={false}
                useCurrentColor
                width={20}
              />
            }
            label={<BottomNavLabel isActive={isHomepage} label={t('breadcrumbs.home')} />}
            onClick={() => navigate('/')}
            value="/"
          />
          <BottomNavigationAction
            icon={
              <FormsLogo
                color={
                  isFormsActive
                    ? theme.palette.common.doss.blue[8]
                    : theme.palette.common.doss.gray[8]
                }
                height="24px"
                width="24px"
              />
            }
            label={<BottomNavLabel isActive={isFormsActive} label={t('breadcrumbs.forms')} />}
            value="/forms"
          />
          <BottomNavigationAction
            icon={
              <SearchIcon
                color={
                  openSearchModal
                    ? theme.palette.common.doss.blue[8]
                    : theme.palette.common.doss.gray[8]
                }
                size={24}
              />
            }
            label={<BottomNavLabel isActive={openSearchModal} label={t('breadcrumbs.search')} />}
            onClick={() => {
              setOpenSearchModal(true)
            }}
            value={openSearchModal ? pathname : '#'}
          />
          <BottomNavigationAction
            icon={
              <AnimatedDossbotIcon
                animate={false}
                className={isDossbotActive ? 'text-blue-8' : 'text-gray-8'}
                height={24}
                width={24}
              />
            }
            label={<BottomNavLabel isActive={isDossbotActive} label={t('breadcrumbs.dossbot')} />}
            value={isDossbotActive ? pathname : '/dossbot/chat'}
          />
          <BottomNavigationAction
            icon={
              <UserIcon
                color={
                  isSettingsActive
                    ? theme.palette.common.doss.blue[8]
                    : theme.palette.common.doss.gray[8]
                }
                size={24}
              />
            }
            label={<BottomNavLabel isActive={isSettingsActive} label={t('breadcrumbs.profile')} />}
            value="/settings"
          />
        </BottomNavigation>
      </Paper>
    </>
  )
}
